import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { CardComponent } from "../../shared/components/card/card.component";
import { CardType } from '../../core/enum/card-type.enum';
import { InformativeTextComponent } from "../../shared/components/informative-text/informative-text.component";
import { Router, ActivatedRoute } from '@angular/router';
import { DecodedToken } from '../../core/models/decoded-token';
import { HeaderComponent } from '../../core/components/header/header.component';
import { HttpClientService } from '../../core/services/http-client.service';
import { ParticipantePlanos } from '../../core/models/participante';
import { LoaderService } from '../../core/services/loader.service';
import { HttpResponse } from '@angular/common/http';
import { EnumStatusResponse } from '../../core/enum/EnumHttpResponse';
import { DocumentDownloadhRequest, DocumentDownloadResult, DocumentSearchRequest, DocumentSearchResult } from '../../core/models/document-GED';
import JSZip from 'jszip';
import { ModalComponent } from "../../shared/components/modal/modal.component";
import { ToastService } from '../../core/services/toast.service';
import { InformativeNoResultComponent } from '../../shared/components/informative-no-result/informative-no-result.component';
import { ExtractService } from 'src/app/core/services/extract.service';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';

export interface DocumentoBase64 {
  nomeArquivo: string;
  base64: string;
}

type InformeContext = {
  visibleHeader: boolean;
  cpfParticipante?: number;
  nomeParticipante?: string;
  token?: DecodedToken | null;
}

@Component({
  selector: 'app-informe-rendimentos',
  standalone: true,
  imports: [CommonModule, CardComponent, InformativeTextComponent, HeaderComponent, ModalComponent, InformativeNoResultComponent],
  templateUrl: './informe-rendimentos.component.html',
  styleUrl: './informe-rendimentos.component.scss'
})
export class InformeRendimentosComponent implements OnInit {
  visibleHeader: boolean = false;
  currentYear = new Date().getFullYear();
  lastYear = this.currentYear - 1;
  tokenCrip: string = '';

  isModalOpen: boolean = false;
  isApp: boolean = false;
  isAttendant: boolean = false;
  isDepSaude: boolean = false;

  cpf?: any;
  name?: string = '';
  cpfFinal?: string;
  featureId?: number;
  private tokenDecoded: any | null = null;

  participantePlano!: ParticipantePlanos;

  // Lista de documentos que vêm do GED (rendimentos). Cada item vira UMA chamada no download.
  private documentInformes: DocumentDownloadhRequest[] = [];

  // Flags que controlam se o card "Informe de Rendimentos" aparece e se o fluxo de download deve buscar cada fonte.
  private informeRendimentos: boolean = false;
  private informeResgate: boolean = false;

  cardType = CardType;

  cardsVisible: Record<CardType, boolean> = {
    [CardType.INFORME_FAMILINVEST]: false,
    [CardType.INFORME_FORD]: false,
    [CardType.INFORME_ROCHEPREV]: false,
    [CardType.INFORME_RENDIMENTOS]: false,
    [CardType.INFORME_CONTRIBUICAO]: false,
    [CardType.EXTRATO_SAUDE]: false
  };

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private httpClientService: HttpClientService,
    private loaderService: LoaderService,
    private toastService: ToastService,
    private localStorageService: LocalStorageService,
    private extractService: ExtractService
  ) {
    this.visibleHeader = this.resolveVisibleHeader();
    this.isApp = localStorage.getItem("IsApp") === "true";
    this.isAttendant = localStorage.getItem("IsAttendant") === "true";

    // decodifica SEM salvar decodedToken
    this.tokenDecoded = this.decodeFromSToken();

    this.isDepSaude = (this.tokenDecoded?.perfil === 'DEPSAUDE');

    // cpfParticipante: token -> fallback (área aberta)
    const cpfFromToken = this.getCpfParticipanteFromToken(this.tokenDecoded);
    const cpfFromStorage = this.localStorageService.get('cpf');

    let cpfFinal = cpfFromToken ?? (cpfFromStorage ? Number(cpfFromStorage) : null);

    if(this.isDepSaude){
      cpfFinal = this.tokenDecoded.cpf;
    }

    if (!cpfFinal) {
      this.router.navigate(["/not-found"]);
      return;
    }

    this.cpf = cpfFinal;

    // nome: token -> fallback (área aberta)
    this.name =
      this.getNomeFromToken(this.tokenDecoded)
      ?? this.localStorageService.get("nome")
      ?? '';
  }

  async ngOnInit(): Promise<void> {
    // durante as verificações iniciais, exibe mensagem no loader
    this.loaderService.showLoader("Consultando arquivos...", true);

    this.tokenCrip = localStorage.getItem('sToken') || '';

    try {
      this.cpfFinal = String(this.cpf).replace(/\D/g, '').padStart(11, '0');
      await Promise.all([
        this.getParticipantePrevidencia(String(this.cpfFinal), false),
        this.existeInformeGED(false),
        this.getInformeResgate(String(this.cpfFinal), this.lastYear, this.tokenCrip, undefined, undefined, undefined, false),
        this.getInformeContribuicao(String(this.cpfFinal), this.lastYear, this.currentYear, { card: true }, false),
        this.existeExtratoSaude(String(this.cpfFinal), this.lastYear, false)
      ]);
    } finally {
      this.loaderService.hideLoader(true);
    }
  }

  // decodifica token
  private decodeFromSToken(): any | null {
    const sToken = localStorage.getItem('sToken');
    if (!sToken) return null;
    try {
      return this.extractService.getDecodedAccessToken(sToken);
    } catch {
      return null;
    }
  }

  private toCpfNumber(value: any): number | null {
    if (value === null || value === undefined) return null;
    const digits = String(value).replace(/\D/g, '');
    return digits ? Number(digits) : null;
  }

  // se não tiver cpfParticipante no token, usa cpf (token do email)
  private getCpfParticipanteFromToken(token: any | null): number | null {
    if (!token) return null;

    const cpf = this.toCpfNumber(token?.cpf ?? token?.Cpf);
    const cpfParticipante = this.toCpfNumber(token?.cpfParticipante ?? token?.CpfParticipante);

    return cpfParticipante ?? cpf;
  }

  private getNomeFromToken(token: any | null): string | null {
    if (!token) return null;
    const nome = token?.nomeParticipante ?? token?.NomeParticipante;
    return nome ? String(nome) : null;
  }

  private resolveVisibleHeader(): boolean {
    const mostrarHeaderParam = this.route.snapshot.queryParamMap.get("MostrarHeader");
    const mostrarHeaderLocal = localStorage.getItem("MostrarHeader");
    return (mostrarHeaderParam ?? mostrarHeaderLocal) === 'true';
  }

  get nomeExibicao(): string {
    return this.transformName(this.name || '');
  }

  get cpfExibicao(): string {
    return this.cpf ? this.transformCpf(String(this.cpf)) : '';
  }

  redirectLoginFacil() {
    this.redirectFacil(String(this.cpfFinal));
  }

  sendEmail(): void {
    const sToken = localStorage.getItem('sToken');
    if (!sToken) {
      this.toastService.show("Não foi possível enviar o e-mail.", "error", 5000);
      return;
    }

    if(!this.tokenDecoded){
      this.toastService.show("Não foi possível enviar o e-mail.", "error", 5000);
      return;
    }

    this.postEnviarEmailComLink(this.tokenDecoded);
  }

  openModal() {
    this.isModalOpen = true;
  }

  closeModal() {
    this.isModalOpen = false;
  }

  handleCard(type: CardType): void {
    switch (type) {
      case CardType.INFORME_RENDIMENTOS:
        this.downloadInformeRendimentos();
        break;
      case CardType.INFORME_CONTRIBUICAO:
        this.downloadInformeContribuicao();
        break;
      case CardType.INFORME_FORD:
        this.redirecionarFord();
        break;
      case CardType.INFORME_FAMILINVEST:
        this.redirecionarFamilinvest();
        break;
      case CardType.INFORME_ROCHEPREV:
        this.redirecionarRochePrev();
        break;
      case CardType.EXTRATO_SAUDE:
        this.downloadExtratoSaude();
        break;
    }
  }

  isAtendente(): boolean {
    if (!this.tokenDecoded) return false;

    if (this.isAttendant) return true;

    if (this.isDepSaude) return false;

    const cpf = String(this.toCpfNumber(this.tokenDecoded?.cpf ?? this.tokenDecoded?.Cpf) ?? '');
    const cpfParticipante = String(this.getCpfParticipanteFromToken(this.tokenDecoded) ?? '');

    // token do email: cpfParticipante == cpf => false
    if (!cpf || !cpfParticipante) return false;

    return cpfParticipante !== cpf;
  }

  transformName(name: string): string {
    if (!name) return '';

    const preposicoes = ['da', 'de', 'do', 'das', 'dos', 'e'];

    return name
      .toLowerCase()
      .split(' ')
      .map((palavra, index) => {
        if (index !== 0 && preposicoes.includes(palavra)) {
          return palavra;
        }

        return palavra.charAt(0).toUpperCase() + palavra.slice(1);
      })
      .join(' ');
  }

  formatCpf(value: string | number): string {
    if (!value) return '';
    const cpf = String(value).replace(/\D/g, '').padStart(11, '0');
    return cpf;
  }

  //transforma cpf em 000.000.000-00
  transformCpf(cpf: string): string {
    let cpfFormat = this.formatCpf(cpf);
    return cpfFormat.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }

  onRedirect() {
    const cpf = this.cpf;
    if (!cpf) {
      //exbir mensagem de erro ao direcionar
      this.toastService.show("No momento, não foi possível redirecionar para o portal, por favor, tente mais tarde.", "error", 5000);
      return
    }
    this.getUrlScafPlus(cpf);
  }

  //Exibição dos cards de Informes FamiliInvest, Ford e RochePrev
  private setCardsVisible(): void {
    const planos = this.participantePlano?.data ?? [];
    if (!planos.length) return;

    for (const plano of planos) {
      if (plano.dataCancelamento) continue;

      switch (plano.patrocinadora.codigo) {
        //outsystems está 105 e codigo 24 ou 500 e codigo 44
        case 105:
          if (plano.codigo === 24) {
            this.cardsVisible[CardType.INFORME_FAMILINVEST] = true;
          }
          break;

        case 500:
          if (plano.codigo === 44) {
            this.cardsVisible[CardType.INFORME_FAMILINVEST] = true;
          }
          break;
        //outsystems: codigo patrocinadora 118
        case 118:
          this.cardsVisible[CardType.INFORME_FORD] = true;
          break;

        //RochePrev
        case 121:
          this.cardsVisible[CardType.INFORME_ROCHEPREV] = true;
          break;

        case 122:
          this.cardsVisible[CardType.INFORME_ROCHEPREV] = true;
          break;

        case 123:
          this.cardsVisible[CardType.INFORME_ROCHEPREV] = true;
          break;
      }
    }
  }

  private downloadInformeRendimentos(): void {
    if (!this.informeRendimentos && !this.informeResgate) {
      console.warn("Nenhum informe disponível");
      return;
    }

    this.loaderService.showLoader();

    const documentosBase64: DocumentoBase64[] = [];
    this.processDownloadSequencial(0, documentosBase64, "informes-rendimentos.zip");
  }

  private downloadInformeContribuicao(): void {
    const cpf = String(this.cpf);
    if (!cpf) return

    this.getInformeContribuicao(String(this.cpfFinal), this.lastYear, this.currentYear,
      {
        onBlob: (blob) => {
          if (!blob || blob.size === 0) {
            this.toastService.show("Nenhum informe de contribuição disponível.", "error", 5000);
            return;
          }

          this.downloadBlob(blob, "informe-contribuicao.pdf");
        }
      });
  }

  private redirecionarFord(): void {
    this.openModal();
  }

  private redirecionarFamilinvest(): void {
    this.openModal();
  }

  private redirecionarRochePrev(): void {
    this.openModal();
  }

  private getFileNameFromResponse(response: HttpResponse<Blob>, fallback: string): string {
    const cd = response.headers.get("content-disposition") ?? '';
    const match = cd.match(/filename\*?=(?:UTF-8''|")?([^";\n]+)"?/i);
    return match?.[1] ? decodeURIComponent(match[1]) : fallback;
  }

  private downloadExtratoSaude(): void {
    this.loaderService.showLoader();

    let cpf;

    if(this.isDepSaude){
      cpf = this.tokenDecoded.cpf;
    } else {
      cpf = this.cpfFinal;
    }

    const cpfExtrato = String(cpf).replace(/\D/g, '').padStart(11, '0');

    this.httpClientService.postExtratoSaude(cpfExtrato, this.lastYear)
      .then((response: HttpResponse<Blob>) => {
        const blob = response.body;

        if (!blob || blob.size === 0) {
          this.toastService.show("Nenhum extrato de saúde disponível.", "error", 5000);
          return;
        }

        const fallback = `extrato-saude-${this.lastYear}.zip`; // se não vier nome
        const fileName = this.getFileNameFromResponse(response, fallback);

        this.downloadBlob(blob, fileName);
      })
      .catch(() => {
        this.toastService.show("Não foi possível baixar o extrato de saúde.", "error", 5000);
      })
      .finally(() => this.loaderService.hideLoader());
  }

  // Baixa os PDFs do GED de forma sequencial. Quando terminar ele tenta baixar o Resgate
  // finaliza gerando o pdf ou zip
  private processDownloadSequencial(
    index: number,
    documentos: DocumentoBase64[],
    nomeZip?: string
  ): void {

    // terminou de baixar resultados do GED
    if (index >= this.documentInformes.length) {
      this.getInformeResgate(String(this.cpfFinal), this.lastYear, this.tokenCrip, true, documentos, nomeZip);
      return;
    }

    const documento = this.documentInformes[index];

    this.getInformeRendimentosGED(index, documento, documentos, nomeZip);
  }

  private processDownloadDocument(documentos: DocumentoBase64[], nomeZip?: string): void {
    if (!documentos.length) {
      console.warn("Nenhum documento para download");
      this.loaderService.hideLoader();
      return;
    }

    // 1 arquivo = PDF direto
    if (documentos.length === 1) {
      const doc = documentos[0];
      this.downloadBlob(this.base64ToBlob(doc.base64), doc.nomeArquivo);
      this.loaderService.hideLoader();
      return;
    }

    // 2+ = arquivos = ZIP
    const zip = new JSZip();
    documentos.forEach(doc => zip.file(doc.nomeArquivo, this.base64ToBlob(doc.base64)));

    zip.generateAsync({ type: "blob" })
      .then(zipBlob => this.downloadBlob(zipBlob, nomeZip ?? "documentos.zip"))
      .catch(error => console.error("Erro ao gerar ZIP"))
      .finally(() => this.loaderService.hideLoader());
  }

  private base64ToBlob(base64: string, contentType = "application/pdf"): Blob {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    return new Blob([new Uint8Array(byteNumbers)], { type: contentType });
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    try {
      if (!blob || blob.size === 0) {
        throw new Error('Arquivo vazio');
      }

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      link.click();

      URL.revokeObjectURL(link.href);

      this.toastService.show("Download iniciado com sucesso.", "success", 3000);

    } catch (error) {
      console.error('Erro ao baixar arquivo');
      this.toastService.show("Erro ao realizar o download do arquivo.", "error", 5000);
    }
  }

  private existeInformeGED(useLoader: boolean = true): Promise<void> {
    const request: DocumentSearchRequest = {
      filters: {
        IDCATEGORY: 'CP14 - IRRFAPO'
      },
      attributes: {
        item: [
          { IDATTRIBUTE: 'NUM-CPF', VLATTRIBUTE: this.formatCpf(this.cpf) },
          { IDATTRIBUTE: 'ANO-BAS', VLATTRIBUTE: String(this.lastYear) }
        ]
      }
    };

    if (useLoader) this.loaderService.showLoader();

    return this.httpClientService.postDocumentSearch(request)
      .then((response: HttpResponse<DocumentSearchResult>) => {

        if (response.status !== EnumStatusResponse.Success) return;

        const raw = response.body?.return?.RESULTS?.item;
        const items = Array.isArray(raw) ? raw : raw ? [raw] : [];

        if (!items.length) return;

        this.informeRendimentos = true;
        this.cardsVisible[CardType.INFORME_RENDIMENTOS] = true;

        this.documentInformes = items
          .filter(item => item.IDDOCUMENT && item.NMFILE)
          .map(item => ({
            iddocument: item.IDDOCUMENT as string,
            nmfile: item.NMFILE as string
          }));
      })
      .catch(error => {
        console.error("Erro ao buscar informe de rendimentos no GED");
      })
      .finally(() => {
        if (useLoader) this.loaderService.hideLoader();
      });
  }

  private getInformeRendimentosGED(
    index: number,
    documento: DocumentDownloadhRequest,
    documentos: DocumentoBase64[],
    nomeZip?: string
  ): void {

    this.httpClientService.postInformeRendimentosGED(documento)
      .then((response: HttpResponse<DocumentDownloadResult>) => {
        if (response.status !== EnumStatusResponse.Success) return;

        const raw = response.body?.return?.item;
        const files = Array.isArray(raw) ? raw : raw ? [raw] : [];

        for (const file of files) {
          if (file?.NMFILE && file?.BINFILE) {
            documentos.push({ nomeArquivo: file.NMFILE, base64: file.BINFILE });
          }
        }
      })
      .catch(error => {
        console.error(`Erro ao baixar Informe Rendimentos GED ${index + 1}`);
      })
      .finally(() => {
        this.processDownloadSequencial(index + 1, documentos, nomeZip);
      });
  }

  private getInformeResgate(cpf: string, lastYear: number, token: string, download?: boolean, documentos?: DocumentoBase64[], nomeZip?: string, useLoader: boolean = true): Promise<void> {
    
    if (!download && useLoader) this.loaderService.showLoader();
    
    return this.httpClientService.getInformeResgate(cpf, lastYear, token)
      .then((response: HttpResponse<Blob>) => {
        
        const blob = response.body;
        const hasFile = response.status ===  EnumStatusResponse.Success && !!blob && blob.size > 0;

        //Checagem do card
        if (!download) {
          this.informeResgate = hasFile;

          if (this.informeResgate) this.cardsVisible[CardType.INFORME_RENDIMENTOS] = true;

          return;
        }

        if (!hasFile || !blob) return;

        //Fluxo de download
        const fileName = "informe-resgate.pdf";

        if (!documentos) {
          this.downloadBlob(blob, fileName);
          return;
        }

        const reader = new FileReader();
        reader.onload = () => {
          const result = String(reader.result);
          const base64 = result.split(',')[1] ?? '';

          documentos.push({
            nomeArquivo: fileName,
            base64
          });

          this.loaderService.hideLoader();
          this.processDownloadDocument(documentos, nomeZip);
        };

        reader.readAsDataURL(blob);
      })
      .catch(error => {
        if (!download) {
          console.error("Erro ao buscar informe de resgate (checagem)");
        } else {
          console.error("Erro ao buscar informe de resgate (download)");
        }
        this.informeResgate = false;
      })
      .finally(() => {
        if (useLoader) this.loaderService.hideLoader();
        if (download && documentos) {
          if (useLoader) this.loaderService.hideLoader();
          this.processDownloadDocument(documentos, nomeZip);
        }
      });
  }

  private getParticipantePrevidencia(cpfParticipante: string, useLoader: boolean = true): Promise<void> {
    if (useLoader) this.loaderService.showLoader();
    return this.httpClientService.getPlanoPrevidencia(cpfParticipante)
      .then((response: HttpResponse<any>) => {
        if (response.status === EnumStatusResponse.Success) {
          this.participantePlano = response.body;
          this.setCardsVisible();
        }
      })
      .catch(error => {
        console.error("Erro ao buscar plano de previdência");
      })
      .finally(() => {
        if (useLoader) this.loaderService.hideLoader();
      });
  }

  private getInformeContribuicao(
    cpf: string,
    lastYear: number,
    currentYear: number,
    enable?: { card?: boolean; onBlob?: (blob: Blob) => void },
    useLoader: boolean = true
  ): Promise<void> {
    if (useLoader) this.loaderService.showLoader();
    return this.httpClientService.getInformeContribuicao(String(cpf), lastYear, currentYear)
      .then((response: HttpResponse<Blob>) => {
        if (response.status !== EnumStatusResponse.Success) return;

        const blob = response.body;
        if (!blob) return;

        if (enable?.card) this.cardsVisible[CardType.INFORME_CONTRIBUICAO] = true;

        enable?.onBlob?.(blob);
      })
      .catch(error => {
        console.error("Erro ao consultar Informe de Contribuição");
      })
      .finally(() => {
        if (useLoader) this.loaderService.hideLoader();
      });
  }

  private getUrlScafPlus(cpf: number): void {
    this.httpClientService.postScafPlusLogin(cpf)
      .then((response: HttpResponse<{ url: string }>) => {

        if (response.status !== EnumStatusResponse.Success) return;

        const url = response.body?.url;
        if (!url) {
          this.toastService.show("No momento, não foi possível redirecionar para o portal, por favor, tente mais tarde.", "error", 5000);
          return;
        }

        this.toastService.show("Redirecionamento iniciado com sucesso.", "success", 5000);
        window.open(url, '_blank', 'noopener,noreferrer');
      })
      .catch(error => {
        this.toastService.show("No momento, não foi possível redirecionar para o portal, por favor, tente mais tarde.", "error", 5000);
        console.error("Erro ao fazer login no ScafPlus");
      })
      .finally(() => {
        this.closeModal();
      });
  }

  private postEnviarEmailComLink(token: string): Promise<void> {
    this.loaderService.showLoader();
    return this.httpClientService.postGerarLinkTokenEmail(token)
      .then((response: HttpResponse<any>) => {
        if (response.status === EnumStatusResponse.Success) {
          this.loaderService.hideLoader();
          this.toastService.show("Link de acesso a tela de Documentos IR enviado com sucesso.", "success", 5000);
        }
      })
      .catch(error => {
        this.loaderService.hideLoader();
        this.toastService.show("Não foi possível enviar o e-mail", "error", 5000);
        console.error("Erro ao enviar email");
      });
  }

  private existeExtratoSaude(cpf: string, ano: number, useLoader: boolean = true): Promise<void> {
    if (useLoader) this.loaderService.showLoader();

    return this.httpClientService.postExtratoSaude(cpf, ano)
      .then((response: HttpResponse<Blob>) => {
        const blob = response.body;

        if (response.status === 200 && !!blob && blob.size > 0) {
          this.cardsVisible[CardType.EXTRATO_SAUDE] = true;
        }
      })
      .catch(() => {
        this.cardsVisible[CardType.EXTRATO_SAUDE] = false;
      })
      .finally(() => {
        if (useLoader) this.loaderService.hideLoader();
      });
  }

  private redirectFacil(cpf: string): void {
    this.httpClientService.redirectFacil(cpf);
  }
}
